import { useEffect, useState } from "react";
import { getDefaultOptionDrop, getTranslateByKey } from "@/main/services/utils";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import { getCulture, useCultureReducer } from "@/main/store/culture";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { KeyValue } from "@/main/models/common";
import Button from "@/main/components/shared/button/button";
import { KOGAreaButtonsModal } from "@/main/components/shared/styles";
import { templateEmailNotificationCompanyApi } from "@/main/api";
import { GetEmailTemplateRequest } from "@/main/models/request/templateEmailNotificationCompanyRequest";
import KogModalFadeComponent from "@/main/components/modal/ModalFadeComponent";
import DOMPurify from "dompurify";
import {
	GetEmailTemplateResponse,
	SearchgridTemplateResponse,
} from "@/main/models/response/templateEmailNotificationCompanyResponse";
import * as S from "./styles";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";

const INITIAL_FORM_STATE = {
	language: null as KeyValue | null,
};
type IModalEmailTemplatesDetailsForm = typeof INITIAL_FORM_STATE;

interface Props {
	isOpen: boolean;
	setIsOpen: (value: boolean) => void;
	templateEmail: SearchgridTemplateResponse;
	guidCountry: string | undefined;
}

export default function ModalEmailTemplatesDetails({ isOpen, templateEmail, guidCountry, setIsOpen }: Props) {
	const dispatch = useDispatch();

	const [isOpenPreview, setIsOpenPreview] = useState<boolean>(false);
	const [isLoadingSave, setIsLoadingSave] = useState<boolean>(false);
	const [safeHtml, setSafeHtml] = useState<string>("");
	const [result, setResult] = useState<GetEmailTemplateResponse | null>(null);

	const { culture: cultureReducer } = useCultureReducer();

	const optionDefaultCultureDrop = getDefaultOptionDrop(cultureReducer.data);

	const { control, handleSubmit } = useForm<IModalEmailTemplatesDetailsForm>({
		mode: "onChange",
		defaultValues: INITIAL_FORM_STATE,
	});

	function handleSubmitSave(data: IModalEmailTemplatesDetailsForm) {
		setIsLoadingSave(true);
		const request: GetEmailTemplateRequest = {
			idTemplateEmailNotification: templateEmail.idTemplateEmailNotification,
			idSystemApplication: templateEmail.idSystemApplication,
			guidLanguage: data.language?.key as string,
			guidCompany: templateEmail?.guidCompany,
		};
		templateEmailNotificationCompanyApi
			.getEmailTemplateFormated(request)
			.then((response) => {
				const dataResult = response.data.result[0];
				setResult(dataResult);
				setIsOpenPreview(true);
				setSafeHtml(DOMPurify.sanitize(dataResult.body));
			})
			.finally(() => setIsLoadingSave(false));
	}

	useEffect(() => {
		if (isOpen && guidCountry) {
			dispatch(getCulture({ guidCountry }));
		}
	}, [guidCountry, isOpen]);

	return (
		<div>
			<S.Container>
				<S.BoxLabel>
					<S.TextScreen>{getTranslateByKey("information.insurer")}:</S.TextScreen>
					<strong>{templateEmail?.insurerName}</strong>
				</S.BoxLabel>
				<S.BoxLabel>
					<S.TextScreen>{getTranslateByKey("reportTemplateHistorical.reportNameField")}:</S.TextScreen>
					<strong>{templateEmail?.templateEmailNotificationName}</strong>
				</S.BoxLabel>
				<S.BoxLabel>
					<S.TextScreen>{getTranslateByKey("information.systemApplication")}:</S.TextScreen>
					<strong>{templateEmail?.systemName}</strong>
				</S.BoxLabel>
				<KogAutoCompleteControlled
					required
					nameCustom="language"
					label={getTranslateByKey("information.language")}
					control={control}
					options={cultureReducer.data}
					isLoading={cultureReducer.isLoading}
					rulesCustom={{ required: true }}
					onChangeCustom={() => {}}
					autoHighlightCustom={!!optionDefaultCultureDrop}
					id="id-templateDetail-select-language"
				/>

				<KOGAreaButtonsModal>
					<SecondaryButton
						id="id-templateDetail-btn-back"
						type="button"
						disabled={isLoadingSave}
						onClick={() => setIsOpen(false)}
					>
						{getTranslateByKey("information.back")}
					</SecondaryButton>
					<Button
						id="id-templateDetail-btn-save"
						type="submit"
						isLoading={isLoadingSave}
						onClick={handleSubmit(handleSubmitSave)}
					>
						{getTranslateByKey("information.preview")}
					</Button>
				</KOGAreaButtonsModal>
			</S.Container>

			<KogModalFadeComponent
				titleModalReducer="id-modal-templateDetail-preview"
				open={isOpenPreview && !!safeHtml}
				setOpen={() => {
					setIsOpenPreview(false);
					setSafeHtml("");
				}}
				title={result?.subject}
				hasBackgroundTitle
				isInModal
			>
				<S.ContainerModalPreview>
					<div dangerouslySetInnerHTML={{ __html: safeHtml }} />
					<KOGAreaButtonsModal>
						<SecondaryButton
							id="id-modal-templateDetail-preview-btn-back"
							type="button"
							onClick={() => {
								setIsOpenPreview(false);
								setSafeHtml("");
							}}
						>
							{getTranslateByKey("information.back")}
						</SecondaryButton>
					</KOGAreaButtonsModal>
				</S.ContainerModalPreview>
			</KogModalFadeComponent>
		</div>
	);
}
